import { useCallback, useEffect, useState } from 'react';

/**
 * Tema claro/oscuro del centro de ayuda.
 *
 * Es independiente del resto del portal porque no hay nada de lo que depender:
 * el portal no tiene modo oscuro. La ayuda si, porque es texto largo y hay
 * quien lo lee mejor en oscuro.
 *
 * Se respeta la preferencia del sistema operativo, que es la senal que si
 * existe, y solo hasta que alguien elija a mano. A partir de ahi manda la
 * persona: haber pulsado el boton es una decision mas explicita que un ajuste
 * del sistema puesto hace meses.
 */

export type DocsTema = 'claro' | 'oscuro';

const CLAVE = 'ecosystem:docs-tema';

const prefiereOscuro = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;

/** Lo guardado, si es valido. Un valor raro en el almacen no debe romper nada. */
function guardado(): DocsTema | null {
  try {
    const valor = window.localStorage.getItem(CLAVE);
    return valor === 'claro' || valor === 'oscuro' ? valor : null;
  } catch {
    // Navegacion privada o almacenamiento bloqueado: se sigue sin preferencia.
    return null;
  }
}

export function useDocsTheme(): { tema: DocsTema; alternar: () => void } {
  const [tema, setTema] = useState<DocsTema>(
    () => guardado() ?? (prefiereOscuro() ? 'oscuro' : 'claro'),
  );

  /*
   * Mientras no haya eleccion propia, seguir al sistema en vivo. Quien cambia
   * su portatil a oscuro al anochecer espera que esto cambie con el, y no en
   * la siguiente recarga.
   */
  useEffect(() => {
    if (guardado()) return undefined;
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return undefined;

    const alCambiar = (evento: MediaQueryListEvent): void =>
      setTema(evento.matches ? 'oscuro' : 'claro');
    media.addEventListener('change', alCambiar);
    return () => media.removeEventListener('change', alCambiar);
  }, []);

  const alternar = useCallback(() => {
    setTema((actual) => {
      const siguiente: DocsTema = actual === 'oscuro' ? 'claro' : 'oscuro';
      try {
        window.localStorage.setItem(CLAVE, siguiente);
      } catch {
        // Si no se puede guardar, el tema vale para esta visita y ya.
      }
      return siguiente;
    });
  }, []);

  return { tema, alternar };
}
